import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent, CardFooter, Button } from '../../../shared/components/ui';
import { usePriceData, useUploadData, useCurrentProject, useDeletePriceData } from '../../../hooks/useProjectData';
import { DollarSign, FileText, CheckCircle, AlertCircle, TrendingUp, TrendingDown, Activity, X, ChevronDown, ChevronUp, Clock, Zap } from 'lucide-react';
import { clsx } from 'clsx';
import { Line } from 'react-chartjs-2';
import { toast } from 'react-hot-toast';
import { useFileUpload } from '../../../shared/hooks/useFileUpload';
import { formatNumber, calculateBasicStats, formatDateRange } from '../../../shared/utils/uploadHelpers';
import { BarraSelector } from './BarraSelector';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

type ChartPeriod = 'day' | 'week' | 'month' | 'year';

interface PriceUploadProps {
  projectId?: string;
}

export function PriceUploadEnhanced({ projectId }: PriceUploadProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File upload hook
  const fileUpload = useFileUpload({
    multiple: true,
    acceptedTypes: ['.csv', '.tsv', '.txt', '.xlsx', '.xls'],
    maxSizeMB: 50,
  });

  // Data hooks
  const { data: prices, isLoading: loadingData, refetch: refetchPrices } = usePriceData(projectId);
  const uploadMutation = useUploadData(projectId, (progress, step) => {
    fileUpload.setUploadProgress(progress);
    fileUpload.setUploadStep(step);
  });
  const deletePriceMutation = useDeletePriceData(projectId);
  const { data: currentProject } = useCurrentProject();

  // Local state
  const [showChart, setShowChart] = useState(false);
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>('week');
  const [validation, setValidation] = useState<any | null>(null);
  const [isClearing, setIsClearing] = useState(false);

  // Load validation from project data
  useEffect(() => {
    setValidation(currentProject?.upload_validation_price || null);
  }, [currentProject]);

  const processPrice = async () => {
    if (fileUpload.files.length === 0) return;

    if (!projectId) {
      fileUpload.setUploadError(t('priceUpload.errors.selectProject'));
      return;
    }

    fileUpload.setUploadError(null);
    fileUpload.setUploadProgress(0);
    fileUpload.setUploadStep(t('priceUpload.upload.startingUpload'));

    try {
      const result = await uploadMutation.mutateAsync({
        files: fileUpload.files,
        type: 'price'
      });

      if (result?.validation) {
        setValidation(result.validation);
      }

      if (!result.success) {
        throw new Error(t('priceUpload.errors.processingFailed'));
      }

      fileUpload.clearFiles();
      setShowChart(true);
      toast.success(t('priceUpload.toast.uploadSuccess', { count: fileUpload.files.length }));
    } catch (error) {
      console.error('Error processing price file:', error);
      fileUpload.setUploadError(error instanceof Error ? error.message : t('priceUpload.errors.processingFailed'));
    } finally {
      fileUpload.setUploadProgress(0);
      fileUpload.setUploadStep('');
    }
  };

  const handleClearPriceData = async () => {
    setIsClearing(true);
    fileUpload.setUploadError(null);

    try {
      const result = await deletePriceMutation.mutateAsync();
      fileUpload.clearFiles();
      setShowChart(false);
      await new Promise(resolve => setTimeout(resolve, 100));
      await refetchPrices();
      toast.success(t('priceUpload.toast.deleteSuccess', { count: result.deleted_count || 0 }));
    } catch (error) {
      console.error('Error clearing price data:', error);
      fileUpload.setUploadError(error instanceof Error ? error.message : t('priceUpload.errors.clearFailed'));
      toast.error(t('priceUpload.errors.clearFailed'));
    } finally {
      setIsClearing(false);
    }
  };

  const getChartData = () => {
    if (!prices?.data) return null;

    let filteredData = [...prices.data];

    switch (chartPeriod) {
      case 'day': filteredData = filteredData.slice(-24); break;
      case 'week': filteredData = filteredData.slice(-168); break;
      case 'month': filteredData = filteredData.slice(-720); break;
    }

    const labels = filteredData.map(p => {
      const date = new Date(p.timestamp);
      if (chartPeriod === 'day') return date.toLocaleTimeString('es-CL', { hour: '2-digit' });
      if (chartPeriod === 'week') return date.toLocaleDateString('es-CL', { weekday: 'short', hour: '2-digit' });
      return date.toLocaleDateString('es-CL', { month: 'short', day: 'numeric' });
    });

    const usdPrices = filteredData.map(p => p.price_usd || p.priceUsd || 0);
    const clpPrices = filteredData.map(p => p.price_clp || p.priceClp || 0);
    const pointRadius = filteredData.length > 100 ? 0 : filteredData.length > 50 ? 1 : filteredData.length > 25 ? 2 : 3;

    return {
      labels,
      datasets: [
        {
          label: 'USD/MWh',
          data: usdPrices,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
          yAxisID: 'y1',
          pointRadius,
          pointHoverRadius: pointRadius + 2,
        },
        ...(clpPrices.some(p => p > 0) ? [{
          label: 'CLP/kWh',
          data: clpPrices,
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4,
          fill: true,
          yAxisID: 'y2',
          pointRadius,
          pointHoverRadius: pointRadius + 2,
        }] : [])
      ]
    };
  };

  const calculateStats = () => {
    if (!prices?.data) return null;

    const usdPrices = prices.data.map(p => p.price_usd || p.priceUsd || 0).filter(p => p > 0);
    const clpPrices = prices.data.map(p => p.price_clp || p.priceClp || 0).filter(p => p > 0);

    if (usdPrices.length === 0) return null;

    const usdStats = calculateBasicStats(usdPrices);
    const clpStats = clpPrices.length > 0 ? calculateBasicStats(clpPrices) : null;

    return {
      avgUsd: usdStats?.avg || 0,
      maxUsd: usdStats?.max || 0,
      minUsd: usdStats?.min || 0,
      avgClp: clpStats?.avg || 0,
      maxClp: clpStats?.max || 0,
      minClp: clpStats?.min || 0,
      volatility: usdStats?.volatility || 0,
      spread: ((usdStats?.max || 0) - (usdStats?.min || 0)) / (usdStats?.avg || 1) * 100,
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { position: 'top' as const, labels: { font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const value = context.raw;
            return `${label}: $${value.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 45, minRotation: 45, font: { size: 10 } } },
      y1: { type: 'linear' as const, display: true, position: 'left' as const, title: { display: true, text: 'USD/MWh', font: { size: 11 } }, ticks: { font: { size: 10 } } },
      y2: { type: 'linear' as const, display: prices?.data?.some(p => (p.price_clp || p.priceClp || 0) > 0), position: 'right' as const, title: { display: true, text: 'CLP/kWh', font: { size: 11 } }, ticks: { font: { size: 10 } }, grid: { drawOnChartArea: false } },
    },
  };

  const stats = calculateStats();
  const chartData = getChartData();
  const isLoading = uploadMutation.isLoading || loadingData;

  return (
    <Card variant={prices ? 'elevated' : 'default'}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-swiss-charcoal">{t('priceUpload.title')}</h3>
              <p className="text-sm text-swiss-stone">{t('priceUpload.subtitle')}</p>
            </div>
          </div>
          {prices && <CheckCircle className="w-5 h-5 text-green-600" />}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!prices?.data || prices.data.length === 0 ? (
          <div
            className={clsx(
              'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
              'hover:border-green-400 hover:bg-green-50/50',
              fileUpload.files.length > 0 ? 'border-green-400 bg-green-50/50' : 'border-swiss-mist'
            )}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={fileUpload.handleDragOver}
            onDrop={fileUpload.handleDrop}
          >
            <FileText className="w-8 h-8 mx-auto text-swiss-stone mb-2" />
            <p className="text-sm font-medium text-swiss-charcoal mb-1">
              {fileUpload.files.length > 0
                ? t('priceUpload.upload.filesSelected', { count: fileUpload.files.length, names: fileUpload.files.map(f => f.name).join(', ') })
                : t('priceUpload.upload.dropZone')}
            </p>
            <p className="text-xs text-swiss-stone">{t('priceUpload.upload.supportedFormats')}</p>
            <p className="text-xs text-green-600 font-medium mt-2">{t('priceUpload.upload.tip')}</p>
            <input ref={fileInputRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" onChange={fileUpload.handleFileSelect} className="hidden" multiple />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Statistics Cards */}
            {stats && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-white rounded-lg border border-swiss-mist p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-swiss-stone">{t('priceUpload.statistics.avgPrice')}</span>
                    <Activity className="w-4 h-4 text-blue-500" />
                  </div>
                  <p className="text-sm font-semibold text-swiss-charcoal">${stats.avgUsd.toFixed(2)} USD</p>
                  {stats.avgClp > 0 && <p className="text-xs text-swiss-stone">${stats.avgClp.toFixed(2)} CLP</p>}
                </div>
                <div className="bg-white rounded-lg border border-swiss-mist p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-swiss-stone">{t('priceUpload.statistics.peakPrice')}</span>
                    <TrendingUp className="w-4 h-4 text-red-500" />
                  </div>
                  <p className="text-sm font-semibold text-swiss-charcoal">${stats.maxUsd.toFixed(2)} USD</p>
                  {stats.maxClp > 0 && <p className="text-xs text-swiss-stone">${stats.maxClp.toFixed(2)} CLP</p>}
                </div>
                <div className="bg-white rounded-lg border border-swiss-mist p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-swiss-stone">{t('priceUpload.statistics.valleyPrice')}</span>
                    <TrendingDown className="w-4 h-4 text-green-500" />
                  </div>
                  <p className="text-sm font-semibold text-swiss-charcoal">${stats.minUsd.toFixed(2)} USD</p>
                  {stats.minClp > 0 && <p className="text-xs text-swiss-stone">${stats.minClp.toFixed(2)} CLP</p>}
                </div>
                <div className="bg-white rounded-lg border border-swiss-mist p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-swiss-stone">{t('priceUpload.statistics.volatility')}</span>
                    <Zap className="w-4 h-4 text-yellow-500" />
                  </div>
                  <p className="text-sm font-semibold text-swiss-charcoal">{stats.volatility.toFixed(1)}%</p>
                  <p className="text-xs text-swiss-stone">{t('priceUpload.statistics.spread')}: {stats.spread.toFixed(1)}%</p>
                </div>
              </div>
            )}

            {/* Barra Selector for YoY price projections (required) */}
            {projectId && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-red-600 uppercase tracking-wide">
                    {t('priceUpload.barraRequired', 'Requerido')}
                  </span>
                </div>
                <BarraSelector
                  projectId={projectId}
                  onBarraChange={(barra) => {
                    if (barra) {
                      toast.success(t('priceUpload.toast.barraSelected', { barra }));
                    }
                  }}
                />
              </div>
            )}

            {/* Chart Controls */}
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                {(['day', 'week', 'month', 'year'] as ChartPeriod[]).map((period) => (
                  <button key={period} onClick={() => setChartPeriod(period)} className={clsx('px-3 py-1 text-xs font-medium rounded-md transition-colors', chartPeriod === period ? 'bg-brand-primary text-white' : 'bg-swiss-pearl text-swiss-stone hover:bg-swiss-mist')}>
                    {t(`priceUpload.chart.period.${period}`)}
                  </button>
                ))}
              </div>
              <button onClick={() => setShowChart(!showChart)} className="text-sm text-brand-primary hover:text-brand-primary/80 flex items-center gap-1">
                {showChart ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showChart ? t('priceUpload.chart.hide') : t('priceUpload.chart.show')}
              </button>
            </div>

            {/* Price Chart */}
            {showChart && chartData && (
              <div className="bg-white rounded-lg border border-swiss-mist p-4">
                <div style={{ height: '300px' }}>
                  <Line data={chartData} options={chartOptions} />
                </div>
              </div>
            )}

            {/* Data Summary */}
            <div className="bg-swiss-pearl rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-swiss-stone" />
                <span className="text-sm font-medium text-swiss-graphite">{t('priceUpload.summary.title')}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-swiss-stone">{t('priceUpload.summary.totalRecords')}:</span>
                  <span className="ml-1 font-medium text-swiss-charcoal">{prices.data?.length || 0}</span>
                </div>
                <div>
                  <span className="text-swiss-stone">{t('priceUpload.summary.dateRange')}:</span>
                  <span className="ml-1 font-medium text-swiss-charcoal">
                    {prices.startDate && prices.endDate ? formatDateRange(prices.startDate, prices.endDate) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Validation Table */}
            {validation && (
              <div className="space-y-4 p-4 bg-swiss-pearl/40 rounded-lg border border-swiss-mist">
                <div className="flex items-center gap-2">
                  <div className="h-px flex-1 bg-swiss-mist"></div>
                  <div className="text-sm font-semibold text-swiss-stone uppercase tracking-wide">{t('priceUpload.validation.title')}</div>
                  <div className="h-px flex-1 bg-swiss-mist"></div>
                </div>
                <div className="p-3 bg-swiss-pearl/40 rounded border border-swiss-mist">
                  <div className="text-xs text-swiss-charcoal">
                    <span className="font-medium">{t('priceUpload.validation.recordsSummary')}:</span> {validation.summary?.source_records} {t('priceUpload.validation.uploaded')} → {validation.summary?.db_records} {t('priceUpload.validation.inDatabase')}
                    {validation.summary?.match && <span className="ml-2 text-brand-primary font-semibold">✓ {t('priceUpload.validation.allMatched')}</span>}
                  </div>
                </div>
                {validation.samples && validation.samples.length > 0 && (
                  <div className="overflow-x-auto rounded border border-swiss-mist">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-swiss-mist bg-white">
                          <th className="px-3 py-2 text-left text-swiss-stone font-medium">{t('priceUpload.validation.sample')}</th>
                          <th className="px-3 py-2 text-left text-swiss-stone font-medium">{t('priceUpload.validation.fileTimestamp')}</th>
                          <th className="px-3 py-2 text-right text-swiss-stone font-medium">{t('priceUpload.validation.filePriceMWh')}</th>
                          <th className="px-3 py-2 text-left text-swiss-stone font-medium">{t('priceUpload.validation.dbTimestamp')}</th>
                          <th className="px-3 py-2 text-right text-swiss-stone font-medium">{t('priceUpload.validation.dbPriceMWh')}</th>
                          <th className="px-3 py-2 text-center text-swiss-stone font-medium">✓</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {validation.samples.map((sample: any, idx: number) => (
                          <tr key={idx} className="border-b border-swiss-mist last:border-0 hover:bg-swiss-pearl/20">
                            <td className="px-3 py-2 text-swiss-stone text-[10px] font-medium">{sample.label}</td>
                            <td className="px-3 py-2 font-mono text-[10px] text-swiss-charcoal">{sample.source_timestamp}</td>
                            <td className="px-3 py-2 text-right text-swiss-charcoal">{sample.source_price_usd_mwh?.toFixed(2)}</td>
                            <td className="px-3 py-2 font-mono text-[10px] text-swiss-charcoal">{sample.db_timestamp}</td>
                            <td className="px-3 py-2 text-right text-swiss-charcoal">{sample.db_price_usd_mwh?.toFixed(2)}</td>
                            <td className="px-3 py-2 text-center">
                              {sample.match ? <span className="text-brand-primary font-bold text-sm">✓</span> : <span className="text-swiss-stone font-bold text-sm">✗</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {fileUpload.uploadError && (
          <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{fileUpload.uploadError}</span>
          </div>
        )}

        {/* Upload Progress */}
        {uploadMutation.isPending && fileUpload.uploadProgress > 0 && (
          <div className="space-y-2 p-4 bg-brand-primary/5 rounded-lg border border-brand-primary/20">
            <div className="flex items-center justify-between text-sm">
              <span className="text-swiss-charcoal font-medium">{fileUpload.uploadStep}</span>
              <span className="text-brand-primary font-semibold">{fileUpload.uploadProgress}%</span>
            </div>
            <div className="w-full h-2 bg-swiss-pearl rounded-full overflow-hidden">
              <div className="h-full bg-brand-primary transition-all duration-300 ease-out" style={{ width: `${fileUpload.uploadProgress}%` }} />
            </div>
          </div>
        )}
      </CardContent>

      {(!prices?.data || prices.data.length === 0) ? (
        <CardFooter>
          <Button className="w-full" onClick={processPrice} disabled={fileUpload.files.length === 0 || isLoading} loading={isLoading}>
            {fileUpload.files.length > 1 ? t('priceUpload.buttons.processMultiple', { count: fileUpload.files.length }) : t('priceUpload.buttons.processSingle')}
          </Button>
        </CardFooter>
      ) : (
        <CardFooter>
          <div className="flex gap-2 w-full">
            <Button className="flex-1" variant="secondary" onClick={handleClearPriceData} disabled={isClearing} loading={isClearing}>
              {t('priceUpload.buttons.replaceFile')}
            </Button>
            <Button className="flex-1" variant="ghost" onClick={() => setShowChart(!showChart)}>
              {showChart ? t('priceUpload.chart.hide') : t('priceUpload.chart.show')}
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>
  );
}
